import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  Database,
  Users,
  Tags,
  Truck,
  UserCheck,
  Send,
  FileOutput,
  ShoppingBag,
  PackageCheck,
  UserCog,
  Link2,
  Shield,
  Palette,
  Layers,
  Package,
  Boxes,
  Warehouse,
  BarChart3,
  Settings,
  Sheet,
  ShieldCheck,
  DatabaseBackup,
  Mail,
  Globe,
  Download,
  Contact,
  ListChecks,
  FileText,
  Receipt,
  Handshake,
  FileSpreadsheet,
  Building2,
  Landmark,
} from 'lucide-react'
import type { PermissionKey } from '@/types/permission.types'
import { ROUTES } from '@/constants/routes'

export interface NavItem {
  id: string
  label: string
  href: string
  icon: LucideIcon
  /** Permission required to see this item */
  permission?: PermissionKey
  /** Sidebar only: shown inside this sub-section of its group (see NavGroup.subgroups). */
  subgroup?: string
}

/** A folding sub-section inside a sidebar group (e.g. Database → Products). */
export interface NavSubgroup {
  id: string
  label: string
  icon: LucideIcon
}

export interface NavGroup {
  id: string
  label: string
  icon: LucideIcon
  /** If set, group is visible when user has ANY child permission or this permission */
  permission?: PermissionKey
  /** Children stay one flat list (titles, workspace, menus read it as is);
   * the sidebar draws items with a matching `subgroup` inside these. */
  subgroups?: NavSubgroup[]
  children: NavItem[]
}

export type SidebarEntry =
  | (NavItem & { type: 'item' })
  | (NavGroup & { type: 'group' })

/** Sidebar entries for features that are built (no Coming Soon stubs). */
export const SIDEBAR_NAV: SidebarEntry[] = [
  {
    type: 'item',
    id: 'dashboard',
    label: 'Dashboard',
    href: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    permission: 'dashboard.view',
  },
  {
    type: 'group',
    id: 'master-data',
    label: 'Database',
    icon: Database,
    subgroups: [{ id: 'products', label: 'Products', icon: Package }],
    children: [
      {
        id: 'master-data-overview',
        label: 'Overview',
        href: ROUTES.MASTER_DATA,
        icon: Database,
        permission: 'master_data.view',
      },
      {
        id: 'customers',
        label: 'Parties',
        href: ROUTES.CUSTOMERS,
        icon: Users,
        permission: 'customers.view',
      },
      {
        id: 'suppliers',
        label: 'Vendors',
        href: ROUTES.SUPPLIERS,
        icon: Truck,
        permission: 'suppliers.view',
      },
      {
        id: 'sales-agents',
        label: 'Agents',
        href: ROUTES.SALES_AGENTS,
        icon: UserCheck,
        permission: 'sales_agents.view',
      },
      {
        id: 'warehouses',
        label: 'Warehouses',
        href: ROUTES.WAREHOUSES,
        icon: Warehouse,
        permission: 'warehouses.view',
      },
      {
        id: 'designs',
        label: 'Designs',
        href: ROUTES.PRODUCTS,
        icon: Package,
        permission: 'products.view',
        subgroup: 'products',
      },
      {
        id: 'categories',
        label: 'Categories',
        href: ROUTES.CATEGORIES,
        icon: Tags,
        permission: 'categories.view',
        subgroup: 'products',
      },
      {
        id: 'qualities',
        label: 'Qualities',
        href: ROUTES.QUALITIES,
        icon: Layers,
        permission: 'qualities.view',
        subgroup: 'products',
      },
      {
        id: 'colours',
        label: 'Colors',
        href: ROUTES.COLOURS,
        icon: Palette,
        permission: 'colors.view',
        subgroup: 'products',
      },
    ],
  },
  {
    type: 'item',
    id: 'catalog',
    label: 'Catalog',
    href: ROUTES.CATALOG,
    icon: Boxes,
    permission: 'catalog.view',
  },
  {
    // Lead → quotation → estimate chain. Phones: one "Sales" tile that opens these.
    type: 'group',
    id: 'sales',
    label: 'Sales',
    icon: Handshake,
    children: [
      {
        id: 'sales-leads',
        label: 'Leads',
        href: ROUTES.SALES_LEADS,
        icon: Contact,
        permission: 'sales_leads.view',
      },
      {
        id: 'quotations-list',
        label: 'Quotations',
        href: ROUTES.QUOTATIONS,
        icon: Receipt,
        permission: 'quotations.view',
      },
      {
        id: 'estimates-list',
        label: 'Estimates',
        href: ROUTES.ESTIMATES,
        icon: FileSpreadsheet,
        permission: 'estimates.view',
      },
      {
        id: 'quotation-terms-templates',
        label: 'Terms',
        href: ROUTES.QUOTATION_TERMS_TEMPLATES,
        icon: FileText,
        permission: 'quotation_terms.view',
      },
    ],
  },
  {
    // Single page → direct link (was a one-child "Dispatch" group).
    type: 'item',
    id: 'dispatch',
    label: 'Dispatches',
    href: ROUTES.DISPATCH_SHEET,
    icon: Send,
    permission: 'delivery_challans.view',
  },
  {
    // Single page → direct link (was a one-child "Purchase" group).
    type: 'item',
    id: 'purchases',
    label: 'Purchases',
    href: ROUTES.PURCHASE_SHEET,
    icon: ShoppingBag,
    permission: 'goods_received.view',
  },
  {
    type: 'item',
    id: 'tasks',
    label: 'Tasks',
    href: ROUTES.TASKS,
    icon: ListChecks,
    permission: 'tasks.view',
  },
  {
    type: 'group',
    id: 'reports',
    label: 'Reports',
    icon: BarChart3,
    children: [
      {
        id: 'dispatch-summary',
        label: 'Dispatch Summary',
        href: ROUTES.REPORTS_DISPATCH_SUMMARY,
        icon: FileOutput,
        permission: 'dispatch_reports.view',
      },
      {
        id: 'purchase-summary',
        label: 'Purchase Summary',
        href: ROUTES.REPORTS_PURCHASE_SUMMARY,
        icon: PackageCheck,
        permission: 'purchase_reports.view',
      },
      {
        id: 'report-exports',
        label: 'Detail Exports',
        href: ROUTES.REPORTS_EXPORTS,
        icon: Download,
        permission: 'reports.view',
      },
    ],
  },
  {
    // Company-wide details used on documents (bank accounts today).
    type: 'group',
    id: 'company',
    label: 'Company',
    icon: Building2,
    children: [
      {
        id: 'bank-accounts',
        label: 'Bank accounts',
        href: ROUTES.COMPANY_BANK_ACCOUNTS,
        icon: Landmark,
        permission: 'bank_accounts.view',
      },
    ],
  },
  {
    type: 'group',
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      {
        id: 'sheets-sync',
        label: 'Sheets Sync',
        href: ROUTES.SETTINGS_SHEETS_SYNC,
        icon: Sheet,
        permission: 'sheets_sync.run',
      },
      {
        id: 'security-settings',
        label: 'Security',
        href: ROUTES.SETTINGS_SECURITY,
        icon: ShieldCheck,
        permission: 'auth_settings.manage',
      },
      {
        id: 'backups',
        label: 'Backups',
        href: ROUTES.SETTINGS_BACKUPS,
        icon: DatabaseBackup,
        permission: 'backups.view',
      },
      {
        id: 'email-settings',
        label: 'Email',
        href: ROUTES.SETTINGS_EMAIL,
        icon: Mail,
        permission: 'mail.view',
      },
      {
        id: 'site-form-settings',
        label: 'Website form',
        href: ROUTES.SETTINGS_SITE_FORM,
        icon: Globe,
        permission: 'site_form.manage',
      },
    ],
  },
  {
    type: 'group',
    id: 'user-management',
    label: 'Users',
    icon: UserCog,
    children: [
      {
        id: 'users',
        label: 'Users',
        href: ROUTES.USERS,
        icon: Users,
        permission: 'users.view',
      },
      {
        id: 'agent-links',
        label: 'Agent links',
        href: ROUTES.USERS_AGENT_LINKS,
        icon: Link2,
        permission: 'sales_agents.link',
      },
      {
        id: 'permissions',
        label: 'Permissions',
        href: ROUTES.PERMISSIONS,
        icon: Shield,
        permission: 'permissions.view',
      },
    ],
  },
]
